/**
 * Settings > SLA. The four deadline rows (one per priority): how long IT has to
 * send a first reply and to resolve, whether the clock counts business hours
 * only, and when the chip turns to "Due soon".
 */

import { useEffect, useState } from 'react';
import { useSla, usePutSla } from '@/lib/useServiceDesk';
import type { SdSlaRow } from '@/lib/serviceDeskTypes';
import { SdError } from './SdShell';
import { ALL_PRIORITIES, PRIORITY_LABELS, formatSpan } from './sdLabels';
import { SD_BTN_PRIMARY, SD_CARD, SD_INPUT, SD_MUTED } from './sdStyles';

export function SlaTab() {
  const sla = useSla();
  const put = usePutSla();
  const [rows, setRows] = useState<SdSlaRow[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!sla.data) return;
    setRows(ALL_PRIORITIES.map((p) => sla.data.find((r) => r.priority === p)).filter((r): r is SdSlaRow => !!r));
  }, [sla.data]);

  const set = (priority: string, patch: Partial<SdSlaRow>) => {
    setSaved(false);
    setRows((cur) => cur.map((r) => (r.priority === priority ? { ...r, ...patch } : r)));
  };

  const valid = rows.every((r) => r.first_response_minutes > 0 && r.resolve_minutes > 0 && r.warn_at_fraction > 0 && r.warn_at_fraction <= 1);

  return (
    <div className={SD_CARD}>
      <p className={`mb-4 text-sm ${SD_MUTED}`}>
        The deadline clock runs while a ticket is New, Waiting on IT, or In progress. It pauses while Waiting on requester,
        Waiting on vendor, On hold, or Pending HR approval, and stops when Resolved or Closed.
      </p>
      {sla.isLoading ? (
        <p className={`text-sm ${SD_MUTED}`}>Loading deadlines...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[12px] font-semibold uppercase tracking-caps-pill text-amp-ink dark:text-amp-dark-ink-muted">
                <th className="py-2 pr-3">Priority</th>
                <th className="py-2 pr-3">First reply within (minutes)</th>
                <th className="py-2 pr-3">Resolved within (minutes)</th>
                <th className="py-2 pr-3">Count business hours only</th>
                <th className="py-2 pr-3">Warn after (percent used)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {rows.map((r) => (
                <tr key={r.priority}>
                  <th scope="row" className="py-2 pr-3 text-left font-semibold text-amp-ink dark:text-amp-dark-ink">
                    {PRIORITY_LABELS[r.priority]}
                  </th>
                  <td className="py-2 pr-3">
                    <input
                      type="number"
                      min={1}
                      aria-label={`${PRIORITY_LABELS[r.priority]} first reply minutes`}
                      value={r.first_response_minutes}
                      onChange={(e) => set(r.priority, { first_response_minutes: Number(e.target.value) })}
                      className={`${SD_INPUT} w-28`}
                    />
                    <span className={`ml-2 text-[12px] ${SD_MUTED}`}>{formatSpan(r.first_response_minutes * 60_000)}</span>
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="number"
                      min={1}
                      aria-label={`${PRIORITY_LABELS[r.priority]} resolve minutes`}
                      value={r.resolve_minutes}
                      onChange={(e) => set(r.priority, { resolve_minutes: Number(e.target.value) })}
                      className={`${SD_INPUT} w-28`}
                    />
                    <span className={`ml-2 text-[12px] ${SD_MUTED}`}>{formatSpan(r.resolve_minutes * 60_000)}</span>
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="checkbox"
                      aria-label={`${PRIORITY_LABELS[r.priority]} business hours only`}
                      checked={r.business_hours_only}
                      onChange={(e) => set(r.priority, { business_hours_only: e.target.checked })}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      aria-label={`${PRIORITY_LABELS[r.priority]} warning percent`}
                      value={Math.round(r.warn_at_fraction * 100)}
                      onChange={(e) => set(r.priority, { warn_at_fraction: Number(e.target.value) / 100 })}
                      className={`${SD_INPUT} w-20`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={!valid || put.isPending || rows.length === 0}
          className={SD_BTN_PRIMARY}
          onClick={() => put.mutate(rows, { onSuccess: () => setSaved(true) })}
        >
          Save deadlines
        </button>
        {saved && <span className={`text-[13px] ${SD_MUTED}`}>Saved.</span>}
      </div>
      <SdError error={sla.error ?? put.error} what="save the deadlines" />
    </div>
  );
}
